import React, { useState, useEffect, useRef, lazy } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import {
  setDropdown,
  setMdDropdown,
} from "../../../redux/utils/dropdownActions";
import { useMediaQuery } from "@react-hook/media-query";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { useUserRoles } from "../../../authentication/authentication";
import { MdDoNotDisturbAlt } from "react-icons/md";
import DropdownPortal from "../../common/DropdownPortal";
import useWindowSize from "../../../utils/useWindowSize";
import { isAdminRole } from "../../../utils/adminChecker";
import { checkAccess } from "../../../utils/usePageAccess";
const { arClass } = require("../../../utils/useArClass");
import typeTranslator from "../../../utils/useTypeTranslator";
import textTranslator from "../../../utils/useTextTranslator";

const Txt = lazy(() => import("../../Text"));

/**
 * Text component for the Dashboard page for translations
 * @param children - The text content to be translated
 * @param className - Optional CSS class names
 * @return Translated text element
 */
const Text = ({ children, className }) => {
  return (
    <Txt page="NAVIGATION" className={className}>
      {children}
    </Txt>
  );
};
export default function DropDown({
  links,
  dropDownName,
  index,
  accessKeys = [],
}) {
  const dispatch = useDispatch();
  const activeDropDown = useSelector((state) => state.utils.activeDropdown);
  const darkMode = useSelector((state) => state.settings.darkMode);
  const sidebarStyle = useSelector((state) => state.settings.sidebarStyle);
  const [isActive, setIsActive] = useState(false);
  const [showMobilePortal, setShowMobilePortal] = useState(false);
  const isSmallWindow = useWindowSize().width <= 1280;
  const location = useLocation();
  
  const roles = useUserRoles();
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage,
  );
  // Create refs for the portal
  const buttonRef = useRef(null);

  useEffect(() => {
    const isActiveLink = links.some((link) => link.url === location.pathname);
    setIsActive(isActiveLink);
  }, [location.pathname, links]);

  const toggleDropdownVisibility = () => {
    if (isSmallWindow) {
      setShowMobilePortal(!showMobilePortal);
    } else {
      dispatch(setDropdown(activeDropDown === index ? null : index));
    }
  };

  const hideMobilePortal = () => {
    setShowMobilePortal(false);
    dispatch(setMdDropdown(null));
  };

  // Stronger light-mode colors and LIGHTER / more visible dark-mode colors
  const getColorScheme = (index) => {
    const schemes = [
      {
        light: {
          bg: "bg-violet-300 text-violet-900",
          color: "text-violet-900",
          subBg: "bg-violet-200/90 backdrop-blur-sm",
          border: "border-violet-400",
          hover: "hover:bg-violet-400",
          active:
            "bg-violet-400 text-violet-900 border-violet-500 shadow-lg",
          iconBg: "bg-violet-700",
          glow: "shadow-lg shadow-violet-500/20",
        },
        dark: {
          bg: "bg-violet-700/30 text-violet-50",
          color: "text-violet-50",
          subBg: "bg-violet-100/30 backdrop-blur-sm",
          border: "border-violet-200",
          hover: "hover:bg-violet-600/32",
          active:
            "bg-violet-500 text-white border-violet-200 shadow-lg",
          iconBg: "bg-violet-600",
          glow: "shadow-lg shadow-violet-600/22",
        },
      },
      {
        light: {
          bg: "bg-emerald-300 text-emerald-900",
          color: "text-emerald-900",
          subBg: "bg-emerald-200/90 backdrop-blur-sm",
          border: "border-emerald-400",
          hover: "hover:bg-emerald-400",
          active:
            "bg-emerald-400 text-emerald-900 border-emerald-500 shadow-lg",
          iconBg: "bg-emerald-700",
          glow: "shadow-lg shadow-emerald-500/18",
        },
        dark: {
          bg: "bg-emerald-700/30 text-emerald-50",
          color: "text-emerald-50",
          subBg: "bg-emerald-100/30 backdrop-blur-sm",
          border: "border-emerald-200",
          hover: "hover:bg-emerald-600/32",
          active:
            "bg-emerald-500 text-white border-emerald-200 shadow-lg",
          iconBg: "bg-emerald-600",
          glow: "shadow-lg shadow-emerald-600/20",
        },
      },
      {
        light: {
          bg: "bg-amber-300 text-amber-900",
          color: "text-amber-900",
          subBg: "bg-amber-200/90 backdrop-blur-sm",
          border: "border-amber-400",
          hover: "hover:bg-amber-400",
          active:
            "bg-amber-400 text-amber-900 border-amber-500 shadow-lg",
          iconBg: "bg-amber-700",
          glow: "shadow-lg shadow-amber-500/18",
        },
        dark: {
          bg: "bg-amber-700/30 text-amber-50",
          color: "text-amber-50",
          subBg: "bg-amber-100/30 backdrop-blur-sm",
          border: "border-amber-200",
          hover: "hover:bg-amber-600/32",
          active:
            "bg-amber-500 text-white border-amber-200 shadow-lg",
          iconBg: "bg-amber-600",
          glow: "shadow-lg shadow-amber-600/20",
        },
      },
      {
        light: {
          bg: "bg-indigo-300 text-indigo-900",
          color: "text-indigo-900",
          subBg: "bg-indigo-200/90 backdrop-blur-sm",
          border: "border-indigo-400",
          hover: "hover:bg-indigo-400",
          active:
            "bg-indigo-400 text-indigo-900 border-indigo-500 shadow-lg",
          iconBg: "bg-indigo-700",
          glow: "shadow-lg shadow-indigo-500/18",
        },
        dark: {
          bg: "bg-indigo-700/30 text-indigo-50",
          color: "text-indigo-50",
          subBg: "bg-indigo-100/30 backdrop-blur-sm",
          border: "border-indigo-200",
          hover: "hover:bg-indigo-600/32",
          active:
            "bg-indigo-500 text-white border-indigo-200 shadow-lg",
          iconBg: "bg-indigo-600",
          glow: "shadow-lg shadow-indigo-600/20",
        },
      },
      {
        light: {
          bg: "bg-cyan-300 text-cyan-900",
          color: "text-cyan-900",
          subBg: "bg-cyan-200/90 backdrop-blur-sm",
          border: "border-cyan-400",
          hover: "hover:bg-cyan-400",
          active:
            "bg-cyan-400 text-cyan-900 border-cyan-500 shadow-lg",
          iconBg: "bg-cyan-700",
          glow: "shadow-lg shadow-cyan-500/18",
        },
        dark: {
          bg: "bg-cyan-700/30 text-cyan-50",
          color: "text-cyan-50",
          subBg: "bg-cyan-100/30 backdrop-blur-sm",
          border: "border-cyan-200",
          hover: "hover:bg-cyan-600/32",
          active:
            "bg-cyan-500 text-white border-cyan-200 shadow-lg",
          iconBg: "bg-cyan-600",
          glow: "shadow-lg shadow-cyan-600/20",
        },
      },
      {
        light: {
          bg: "bg-rose-300 text-rose-900",
          color: "text-rose-900",
          subBg: "bg-rose-200/90 backdrop-blur-sm",
          border: "border-rose-400",
          hover: "hover:bg-rose-400",
          active:
            "bg-rose-400 text-rose-900 border-rose-500 shadow-lg",
          iconBg: "bg-rose-700",
          glow: "shadow-lg shadow-rose-500/18",
        },
        dark: {
          bg: "bg-rose-700/30 text-rose-50",
          color: "text-rose-50",
          subBg: "bg-rose-100/30 backdrop-blur-sm",
          border: "border-rose-200",
          hover: "hover:bg-rose-600/32",
          active:
            "bg-rose-500 text-white border-rose-200 shadow-lg",
          iconBg: "bg-rose-600",
          glow: "shadow-lg shadow-rose-600/20",
        },
      },
      {
        light: {
          bg: "bg-teal-300 text-teal-900",
          color: "text-teal-900",
          subBg: "bg-teal-200/90 backdrop-blur-sm",
          border: "border-teal-400",
          hover: "hover:bg-teal-400",
          active:
            "bg-teal-400 text-teal-900 border-teal-500 shadow-lg",
          iconBg: "bg-teal-700",
          glow: "shadow-lg shadow-teal-500/18",
        },
        dark: {
          bg: "bg-teal-700/30 text-teal-50",
          color: "text-teal-50",
          subBg: "bg-teal-100/30 backdrop-blur-sm",
          border: "border-teal-200",
          hover: "hover:bg-teal-600/32",
          active:
            "bg-teal-500 text-white border-teal-200 shadow-lg",
          iconBg: "bg-teal-600",
          glow: "shadow-lg shadow-teal-600/20",
        },
      },
      {
        light: {
          bg: "bg-orange-300 text-orange-900",
          color: "text-orange-900",
          subBg: "bg-orange-200/90 backdrop-blur-sm",
          border: "border-orange-400",
          hover: "hover:bg-orange-400",
          active:
            "bg-orange-400 text-orange-900 border-orange-500 shadow-lg",
          iconBg: "bg-orange-700",
          glow: "shadow-lg shadow-orange-500/16",
        },
        dark: {
          bg: "bg-orange-700/30 text-orange-50",
          color: "text-orange-50",
          subBg: "bg-orange-100/30 backdrop-blur-sm",
          border: "border-orange-200",
          hover: "hover:bg-orange-600/30",
          active:
            "bg-orange-500 text-white border-orange-200 shadow-lg",
          iconBg: "bg-orange-600",
          glow: "shadow-lg shadow-orange-600/20",
        },
      },
    ];

    const entry = schemes[index % schemes.length];
    return darkMode ? entry.dark : entry.light;
  };

  const colors = getColorScheme(index);

  // adapt classes to sidebarStyle (matches AdminSideNav behavior)
  const applySidebarStyle = (colors) => {
    if (
      sidebarStyle === "default" ||
      !sidebarStyle ||
      sidebarStyle ===
        "no-bg-no-icon-colors-no-text-colors-no-dropdown-colors-no-border"
    ) {
      const textColor = darkMode ? "text-white" : "text-black";
      const iconColor = darkMode ? "text-white" : "text-black";
      const hoverBg = darkMode
        ? "hover:bg-gray-600/20"
        : "hover:bg-gray-300/70";
      const activeBg = darkMode ? "bg-gray-700/40" : "bg-gray-300/70";
      const subBg = darkMode ? "bg-gray-900/20" : "bg-gray-100/50";

      return {
        containerActive: `${activeBg} ${textColor} shadow-none border-0`,
        containerInactive: `bg-transparent ${textColor} ${hoverBg} shadow-none border-0`,
        subBg: subBg,
        iconBg: "bg-transparent",
        iconText: iconColor,
        textColor: textColor,
      };
    }

    if (sidebarStyle === "neon") {
      return {
        containerActive: `${colors.active} ${colors.border} ${colors.glow} border border-l-[5px]`,
        containerInactive: `${colors.bg} ${colors.border} ${colors.hover} border hover:shadow-lg`,
        subBg: colors.subBg,
        iconBg: colors.iconBg,
        iconText: "text-white",
        textColor: colors.color,
      };
    }

    if (sidebarStyle === "no-bg-no-border") {
      return {
        containerActive: `${colors.active} ${colors.border} ${colors.glow} shadow-none !border-0`,
        containerInactive: `bg-transparent ${colors.hover} text-gray-800 dark:text-gray-200 hover:shadow-none`,
        subBg: `${colors.subBg} border-l-[5px] shadow-none !border-t-0 !border-b-0 !border-r-0`,
        iconBg: colors.iconBg,
        iconText: "text-white",
        textColor: colors.color,
      };
    }

    if (sidebarStyle === "bg-no-border") {
      return {
        containerActive: `${colors.active} ${colors.glow} `,
        containerInactive: `${colors.bg} ${colors.hover} hover:shadow-lg`,
        subBg: `${colors.subBg} border-l-[5px] shadow-none !border-t-0 !border-b-0 !border-r-0`,
        iconBg: colors.iconBg,
        iconText: "text-white",
        textColor: colors.color,
      };
    }

    return {
      containerActive: `${colors.active} ${colors.border} ${colors.glow} border border-l-[5px]`,
      containerInactive: `${colors.bg} ${colors.border} ${colors.hover}`,
      subBg: colors.subBg,
      iconBg: colors.iconBg,
      iconText: "text-white",
      textColor: colors.color,
    };
  };

  const styled = applySidebarStyle(colors);

  const isExpanded = index === activeDropDown;

  // Calculate approximate max-height based on number of links
  const itemHeight = 48;
  const dropdownMaxHeight = links.length * itemHeight + 16;

  // Smooth animation variants
  const dropdownVariants = {
    hidden: {
      opacity: 0,
      maxHeight: 0,
      transition: {
        duration: 0.2,
        ease: "easeInOut",
      },
    },
    visible: {
      opacity: 1,
      maxHeight: dropdownMaxHeight,
      transition: {
        duration: 0.2,
        ease: "easeInOut",
        staggerChildren: 0.06,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: 4 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 4 },
  };

  const chevronVariants = {
    up: {
      rotate: 180,
      scale: 1.1,
      transition: { type: "spring", stiffness: 500, damping: 20 },
    },
    down: {
      rotate: 0,
      scale: 1.1,
      transition: { type: "spring", stiffness: 500, damping: 20 },
    },
  };

  const containerVariants = {
    hidden: { transition: { staggerChildren: 0.03, staggerDirection: -1 } },
    visible: { transition: { staggerChildren: 0.05 } },
  };

  const chevronColor =
    sidebarStyle === "default" ||
    !sidebarStyle ||
    sidebarStyle ===
      "no-bg-no-icon-colors-no-text-colors-no-dropdown-colors-no-border"
      ? darkMode
        ? "text-white"
        : "text-black"
      : "text-gray-500";

  const resolveAccessKey = (method, fallbackIndex) => {
    if (method) {
      const normalizedMethod = method.toLowerCase();
      const matchedKey = accessKeys.find((key) =>
        key.toLowerCase().startsWith(normalizedMethod),
      );
      if (matchedKey) return matchedKey;
    }
    return accessKeys[fallbackIndex];
  };

  const hasAnyAccess =
    isAdminRole(roles) ||
    accessKeys.length === 0 ||
    accessKeys.some((accessKey) => checkAccess(accessKey));

  // Mobile Portal Dropdown Content
  const SmallPortalDropdown = () => {
    return (
      <motion.div
        initial={{ opacity: 0, y: -6, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -6, scale: 0.98 }}
        transition={{ duration: 0.15, ease: "easeOut" }}
        className={arClass("w-full")}
      >
        {/* Header */}
        <div
          className={arClass(
            `p-4 border-b ${darkMode ? "border-gray-700" : "border-gray-200"}`,
          )}
        >
          <div className={arClass("flex items-center justify-between mb-3")}>
            <h3
              className={arClass(
                `font-semibold text-sm ${
                  darkMode ? "text-white" : "text-gray-900"
                }`,
              )}
            >
              <Text>{dropDownName}</Text>
            </h3>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={hideMobilePortal}
              className={arClass(
                `p-1 rounded-lg text-xs ${
                  darkMode
                    ? "hover:bg-gray-700 text-gray-400"
                    : "hover:bg-gray-100 text-gray-500"
                }`,
              )}
            >
              <svg
                className={arClass("w-4 h-4")}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </motion.button>
          </div>
        </div>

        {/* Content */}
        <div className={arClass("max-h-64 overflow-y-auto p-2")}>
          {links.map((link, idx) => {
            const accessKey = resolveAccessKey(link.method, idx);
            const hasLinkAccess =
              isAdminRole(roles) ||
              !accessKey ||
              checkAccess(accessKey);
            if (!hasLinkAccess) {
              return (
                <div
                  key={idx}
                  className={arClass(
                    `flex items-center gap-3 px-3 py-3 mx-1 rounded-lg text-sm opacity-50 cursor-not-allowed ${
                      darkMode ? "text-red-400" : "text-red-600"
                    }`,
                  )}
                >
                  {link.icon &&
                    React.cloneElement(link.icon, {
                      className: arClass("text-xs flex items-center"),
                    })}
                  <span
                    className={arClass(
                      `relative  font-medium inline-flex items-center gap-1 ${link.name.length > 200 ? "group" : ""}`,
                    )}
                  >
                    {link.name.length > 200 ? (
                      `${link.name.slice(0, 200)}...`
                    ) : (
                      <Text>{link.name}</Text>
                    )}
                    <MdDoNotDisturbAlt />
                    <span
                      className={arClass(
                        "absolute left-1/2 -translate-x-1/2 -top-5 hidden group-hover:block whitespace-nowrap bg-black text-white text-xs px-2 py-1 rounded shadow-lg z-[10000]",
                      )}
                    >
                      You dont have permission
                    </span>
                  </span>
                </div>
              );
            }

            const mobileDropdownItemActiveClass =
              sidebarStyle === "default" ||
              !sidebarStyle ||
              sidebarStyle ===
                "no-bg-no-icon-colors-no-text-colors-no-dropdown-colors-no-border"
                ? darkMode
                  ? " text-white"
                  : " text-black"
                : `${colors.active} ${colors.border} ${colors.glow}`;

            const mobileDropdownItemInactiveClass =
              sidebarStyle === "default" ||
              !sidebarStyle ||
              sidebarStyle ===
                "no-bg-no-icon-colors-no-text-colors-no-dropdown-colors-no-border"
                ? darkMode
                  ? "text-white hover:bg-gray-800/30"
                  : "text-black hover:bg-gray-200/50"
                : `text-gray-600 dark:text-gray-300 hover:${colors.hover} ${colors.border}`;

            const mobileIconColor =
              sidebarStyle === "default" ||
              !sidebarStyle ||
              sidebarStyle ===
                "no-bg-no-icon-colors-no-text-colors-no-dropdown-colors-no-border"
                ? darkMode
                  ? "text-white"
                  : "text-black"
                : "text-black ";

            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.01 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <NavLink
                  to={link.url}
                  onClick={hideMobilePortal}
                  className={({ isActive }) =>
                    arClass(
                      `flex items-center gap-3 px-3 py-3 mx-1 rounded-lg text-sm w-full transition-all duration-200 ${
                        isActive
                          ? mobileDropdownItemActiveClass
                          : mobileDropdownItemInactiveClass
                      }`,
                    )
                  }
                >
                  {link.icon &&
                    React.cloneElement(link.icon, {
                      className: arClass(
                        `${darkMode ? "text-white" : "text-black"} text-xs flex items-center`,
                      ),
                    })}
                  <span className={arClass(`font-medium text-xs ${darkMode ? "text-white" : "text-black"}`)}>
                    <Text>{link.name}</Text>
                  </span>
                </NavLink>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    );
  };

  const PermissionsDropDown = () => {
    if (!hasAnyAccess) {
      return null;
    }

    return (
      <>
        <motion.div
          ref={buttonRef}
          className={arClass(
            `drop-down flex flex-col items-center justify-between gap-1 p-1 rounded-xl text-sm font-medium w-full text-left transition-all duration-300 ${
              isActive || isExpanded || showMobilePortal
                ? styled.containerActive
                : styled.containerInactive
            }`,
          )}
        >
          {/* Main dropdown button */}
          <motion.button
            className={arClass(
              "flex items-center justify-between gap-1 p-1 rounded-xl text-sm font-medium w-full text-left transition-all duration-300",
            )}
            onClick={toggleDropdownVisibility}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            <div className={arClass("flex items-center gap-3")}>
              <motion.div
                className={arClass(
                  `w-8 h-8 rounded-lg flex items-center justify-center ${
                    styled.iconBg
                  } ${
                    sidebarStyle === "default" ||
                    !sidebarStyle ||
                    sidebarStyle ===
                      "no-bg-no-icon-colors-no-text-colors-no-dropdown-colors-no-border"
                      ? ""
                      : "shadow-lg"
                  }`,
                )}
                whileHover={{
                  scale: 1.1,
                  rotate: 5,
                  transition: { type: "spring", stiffness: 400, damping: 10 },
                }}
              >
                {links[0]?.icon &&
                  React.cloneElement(links[0].icon, {
                    className: arClass(`${styled.iconText} text-sm`),
                  })}
              </motion.div>
              <motion.span
                className={arClass(
                  `font-semibold max-xl:hidden ${styled.textColor}`,
                )}
                whileHover={{ x: 2 }}
              >
                <Text>{dropDownName}</Text>
              </motion.span>
            </div>
            <motion.span
              className={arClass(`max-xl:hidden ${chevronColor}`)}
              variants={chevronVariants}
              animate={isExpanded ? "up" : "down"}
              initial={false}
            >
              <FaChevronDown className="text-xs" />
            </motion.span>
          </motion.button>

          {/* Desktop dropdown content */}
          {!isSmallWindow && (
            <AnimatePresence mode="wait">
              {isExpanded && (
                <motion.div
                  className={arClass(
                    "menu flex flex-col gap-1 px-1 py-2 rounded-xl w-full overflow-hidden",
                  )}
                  variants={dropdownVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  style={{ originX: 0.5, originY: 0 }}
                >
                  <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    className={arClass("flex flex-col gap-1")}
                  >
                    {links.map((link, idx) => {
                      const dropdownItemActiveClass =
                        sidebarStyle === "default" ||
                        !sidebarStyle ||
                        sidebarStyle ===
                          "no-bg-no-icon-colors-no-text-colors-no-dropdown-colors-no-border"
                          ? darkMode
                            ? "bg-gray-600/50 text-white"
                            : "bg-gray-300/90 text-black"
                          : `${colors.color} ${styled.subBg} border border-l-[5px] ${colors.border} shadow-lg`;

                      const dropdownItemInactiveClass =
                        sidebarStyle === "default" ||
                        !sidebarStyle ||
                        sidebarStyle ===
                          "no-bg-no-icon-colors-no-text-colors-no-dropdown-colors-no-border"
                          ? darkMode
                            ? "hover:bg-gray-700/40 text-white"
                            : "hover:bg-gray-300/70 text-black"
                          : `${styled.subBg} hover:${colors.active} hover:shadow-sm`;

                      const dropdownIconColor =
                        sidebarStyle === "default" ||
                        !sidebarStyle ||
                        sidebarStyle ===
                          "no-bg-no-icon-colors-no-text-colors-no-dropdown-colors-no-border"
                          ? darkMode
                            ? "text-white"
                            : "text-black"
                          : colors.color;

                      const accessKey = resolveAccessKey(link.method, idx);
                      const hasLinkAccess =
                        isAdminRole(roles) ||
                        !accessKey ||
                        checkAccess(accessKey);
                      if (!hasLinkAccess) {
                        return (
                          <motion.div key={idx} variants={itemVariants}>
                            <div
                              className={arClass(
                                `flex items-center gap-3 px-2 py-3 rounded-lg text-sm opacity-50 cursor-not-allowed ${
                                  darkMode ? "text-red-400" : "text-red-600"
                                }`,
                              )}
                            >
                              <motion.div
                                whileHover={{ scale: 1.1, rotate: 5 }}
                                transition={{
                                  type: "spring",
                                  stiffness: 400,
                                  damping: 10,
                                }}
                              >
                                {React.cloneElement(link.icon, {
                                  className: arClass(
                                    `text-sm flex items-center`,
                                  ),
                                })}
                              </motion.div>
                              <span
                                className={arClass(
                                  "relative group max-xl:hidden font-medium inline-flex items-center gap-1",
                                )}
                              >
                                {link.name.length > 8
                                  ? `${textTranslator(link.name, "NAVIGATION").slice(0, 12)}...`
                                  : textTranslator(link.name, "NAVIGATION")}
                                <MdDoNotDisturbAlt />
                                <span
                                  className={arClass(
                                    "absolute left-1/2 -translate-x-1/2 -top-5 hidden group-hover:block whitespace-nowrap bg-black text-white text-xs px-2 py-1 rounded shadow-lg z-[10000]",
                                  )}
                                >
                                  {link.name}
                                </span>
                              </span>
                            </div>
                          </motion.div>
                        );
                      }

                      return (
                        <motion.div key={idx} variants={itemVariants}>
                          <NavLink
                            className={({ isActive }) =>
                              arClass(
                                `flex items-center gap-3 px-2 py-3 rounded-lg text-sm transition-all duration-300 ${
                                  isActive
                                    ? dropdownItemActiveClass
                                    : dropdownItemInactiveClass
                                }`,
                              )
                            }
                            to={link.url}
                          >
                            <motion.div
                              whileHover={{ scale: 1.1, rotate: 5 }}
                              transition={{
                                type: "spring",
                                stiffness: 400,
                                damping: 10,
                              }}
                            >
                              {React.cloneElement(link.icon, {
                                className: arClass(
                                  `text-sm ${dropdownIconColor} flex items-center`,
                                ),
                              })}
                            </motion.div>
                            <span
                              className={arClass(
                                `relative max-xl:hidden font-medium inline-flex items-center gap-1 ${
                                  link.name.length > 8 ? "group" : ""
                                } ${styled.textColor}`,
                              )}
                            >
                              {link.name.length > 8
                                ? `${textTranslator(link.name, "NAVIGATION").slice(0, 12)}...`
                                : textTranslator(link.name, "NAVIGATION")}
                              <span
                                className={arClass(
                                  "absolute left-1/2 -translate-x-1/2 -top-5 hidden group-hover:block whitespace-nowrap bg-black text-white text-xs px-2 py-1 rounded shadow-lg z-[10000]",
                                )}
                              >
                                <Text>{link.name}</Text>
                              </span>
                            </span>
                          </NavLink>
                        </motion.div>
                      );
                    })}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </motion.div>

        {/* Mobile portal dropdown - using the same pattern as TimePicker */}
        {isSmallWindow && showMobilePortal && (
          <DropdownPortal
            anchorRef={buttonRef}
            onClose={hideMobilePortal}
            className={arClass(
              `mt-2 rounded-md shadow-2xl border z-[1000] !w-[300px] top-0  ${
                darkMode
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              }`,
            )}
            topIncrement={-60}
            align={defaultLanguage === "ar" ? "right" : "left"}
            offset={defaultLanguage === "ar" ? -50 : 50}
          >
            <SmallPortalDropdown />
          </DropdownPortal>
        )}
      </>
    );
  };

  return <PermissionsDropDown />;
}
